import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshPhysicalNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Glass-like transmission preset on `MeshPhysicalNodeMaterial` (IOR, thickness, env specular). */
@EngineClass('TransmissionNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Transmission (Transmissive PBR)',
  nodeMaterialGroup: 'TSL Node FX',
})
export class TransmissionNodeMaterialAsset extends NodeMaterialAsset(MeshPhysicalNodeMaterial) {

  @property({ type: 'color', description: 'Base color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Transmission' })
  override transmission = 1.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Opacity' })
  override opacity = 1.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Metalness' })
  override metalness = 0.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.0;

  @property({ type: 'number', min: 1.0, max: 2.0, step: 0.01, description: 'Index of refraction (IOR)' })
  override ior = 1.5;

  @property({ type: 'number', min: 0.0, max: 5.0, step: 0.01, description: 'Thickness' })
  override thickness = 0.01;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Specular intensity' })
  override specularIntensity = 1.0;

  @property({ type: 'color', description: 'Specular color' })
  override specularColor = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Environment map intensity' })
  override envMapIntensity = 1.0;

  private readonly _metalnessUniform = TSL.uniform(this.metalness);
  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _transmissionUniform = TSL.uniform(this.transmission);
  private readonly _thicknessUniform = TSL.uniform(this.thickness);
  private readonly _iorUniform = TSL.uniform(this.ior);
  private readonly _specularIntensityUniform = TSL.uniform(this.specularIntensity);
  private readonly _specularColorUniform = TSL.uniform(this.specularColor);

  constructor() {
    super();
    this.side = THREE.DoubleSide;
    this.transparent = true;
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.transmission = this.clampFiniteNumber(this.transmission, 0, 1, 1.0);
    this.opacity = this.clampFiniteNumber(this.opacity, 0, 1, 1.0);
    this.metalness = this.clampFiniteNumber(this.metalness, 0, 1, 0.0);
    this.roughness = this.clampFiniteNumber(this.roughness, 0, 1, 0.0);
    const authoredIor = this.ior;
    this.ior =
      typeof authoredIor === 'number' && Number.isFinite(authoredIor) && authoredIor >= 1 && authoredIor <= 2
        ? authoredIor
        : 1.5;
    this.thickness = this.floorFiniteNumber(this.thickness, 0, 0.01);
    this.specularIntensity = this.clampFiniteNumber(this.specularIntensity, 0, 1, 1.0);
    this.envMapIntensity = this.floorFiniteNumber(this.envMapIntensity, 0, 1.0);
    this.colorNode = null;

    this._metalnessUniform.value = this.metalness;
    this._roughnessUniform.value = this.roughness;
    this._transmissionUniform.value = this.transmission;
    this._thicknessUniform.value = this.thickness;
    this._iorUniform.value = this.ior;
    this._specularIntensityUniform.value = this.specularIntensity;
    this._specularColorUniform.value.copy(this.coerceColor(this.specularColor, '#ffffff'));

    this.metalnessNode = this._metalnessUniform;
    this.roughnessNode = this._roughnessUniform;
    (this as any).transmissionNode = this._transmissionUniform;
    (this as any).thicknessNode = this._thicknessUniform;
    (this as any).iorNode = this._iorUniform;
    (this as any).specularIntensityNode = this._specularIntensityUniform;
    (this as any).specularColorNode = this._specularColorUniform;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): TransmissionNodeMaterialAsset {
    const instance = new TransmissionNodeMaterialAsset();
    TransmissionNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
